import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import {
  FilterOptions,
  useServerFilterOptions,
} from '@app/components/data-table/hooks/use-server-filter-options';
import { DataTableProps } from '@app/components/data-table/types/data-table';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import {
  ElectricityActivitiesList,
  PaginatedElectricityActivitiesListList,
} from '@shared/api/types';
import AddDataMenu from '@app/components/imports/scope-1-2-import/components/add-data-menu';
import { baseTableSchema } from '@shared/components/data-table/schema';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { useCCF } from '../../ccf-context';
import ElectricityEditForm from './electricity-edit-form';
import ElectricityPreviewSheet from './electricity-preview-sheet';
import { electricityTableColumns } from './electricity-table-columns';

export const schema = baseTableSchema.extend({
  uuid: z.string().optional(),
});

interface ElectricityTableProps
  extends DataTableProps<PaginatedElectricityActivitiesListList> {}

export default function ElectricityTable({
  data,
  isLoading,
  isPreviousData,
  isFetchedAfterMount,
  status,
}: ElectricityTableProps) {
  const { scope3Supported } = useCCF();

  const [selectedActivityUuid, setSelectedActivityUuid] =
    useState<ElectricityActivitiesList['uuid']>();
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));

  const {
    showSimpleDialog: showElectricityDialog,
    SimpleDialogComponent: ElectricityDialog,
    closeSimpleDialog: closeElectricityDialog,
  } = useSimpleDialog();

  const { results: activities, count } = data || {};

  const pageCount = count ? Math.ceil(count / (perPage || 10)) : 0;

  const { timePeriodsFilter, sitesFilter } = useServerFilterOptions(
    ['timePeriods', 'sites'] as const,
    data?.filters || {}
  );

  const useFilterOptions: FilterOptions<['timePeriods', 'sites']> = useMemo(
    () => ({
      timePeriods: timePeriodsFilter,
      sites: sitesFilter,
    }),
    [timePeriodsFilter, sitesFilter]
  );

  const { table, context } = useServerDataTable({
    data: activities || [],
    columns: electricityTableColumns({
      filters: useFilterOptions,
    }),
    pageCount,
    getRowId: (row) => row.uuid,
    initialState: {
      columnVisibility: {
        uuid: false,
        period: false,
        scope3Emissions: !!scope3Supported,
      },
    },
  });

  return (
    <>
      <DataTable
        table={table}
        context={context}
        className="bg-gray-00"
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        onRowClick={(row) => {
          setSelectedActivityUuid(row?.original.uuid);
          showElectricityDialog();
        }}
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
        tableLoading={!data && status === 'loading'}
        columnClassName={{
          scope3Emissions: 'border-l',
        }}
      >
        <DataTableToolbar table={table} filterOrder={['site', 'period']}>
          <ElectricityPreviewSheet />
          <AddDataMenu
            categorySlug="electricity"
            onAddManually={() => {
              setSelectedActivityUuid(undefined);
              showElectricityDialog();
            }}
          />
        </DataTableToolbar>
      </DataTable>
      {ElectricityDialog({
        header: 'Add data',
        content: (
          <ElectricityEditForm
            activityUuid={selectedActivityUuid}
            closeDialog={closeElectricityDialog}
          />
        ),
      })}
    </>
  );
}
